// (no types needed from ./types here)

type QueuedHttp = { post: Function };
type ApiPathLike = { table: { getSummarizeDataUrl: (id: string | number) => string } };

type LocalizeLike = {
  getLocalizedString: (key: string) => string;
  getLocalizedStringWithTokenReplacement: (key: string, tokens: any) => string;
};

type ChartBackendLike = {
  getRepresentation: (data: any, config: any) => any[];
};

export class CombinationChartBackendServiceTS {
  private readonly queuedHttp: QueuedHttp;
  private readonly apiPath: ApiPathLike;
  private readonly localize: LocalizeLike;
  private readonly barChartBackendService: ChartBackendLike;
  private readonly lineChartBackendService: ChartBackendLike;

  constructor(
    queuedHttp: QueuedHttp,
    apiPath: ApiPathLike,
    localize: LocalizeLike,
    barChartBackendService: ChartBackendLike,
    lineChartBackendService: ChartBackendLike
  ) {
    this.queuedHttp = queuedHttp;
    this.apiPath = apiPath;
    this.localize = localize;
    this.barChartBackendService = barChartBackendService;
    this.lineChartBackendService = lineChartBackendService;
  }

  async fetchJson(
    tableId: string | number,
    chartIndex: number,
    barChartRequest: any,
    lineChartRequest: any
  ) {
    const barUrl = this.apiPath.table.getSummarizeDataUrl(tableId) + "?chart_index=" + chartIndex + "&type=combo_bar";
    const lineUrl =
      this.apiPath.table.getSummarizeDataUrl(tableId) + "?chart_index=" + chartIndex + "&type=combo_line";
    const [barChartResponse, lineChartResponse] = await Promise.all([
      this.queuedHttp.post(barUrl, barChartRequest),
      this.queuedHttp.post(lineUrl, lineChartRequest),
    ]);

    return {
      data: {
        result: {
          data: [barChartResponse.data.result.data, lineChartResponse.data.result.data],
        },
      },
    };
  }

  getRepresentation(data: any[], dataConfig: any) {
    const barChartDataConfig = this.getDataConfigByType(dataConfig, "BarChart");
    let barChartRepresentation = this.barChartBackendService.getRepresentation(data[0], barChartDataConfig);
    barChartRepresentation = barChartRepresentation.map(d => ({
      ...d,
      type: "column",
      legendDisplayName: d.key || "",
    }));

    const lineChartDataConfig = this.getDataConfigByType(dataConfig, "LineChart");
    const lineChartAggregationType = this.getLocalizedAggregationType(
      lineChartDataConfig.chartValue.aggregationType,
      lineChartDataConfig.chartValue.displayName
    );
    let lineChartRepresentation = this.lineChartBackendService.getRepresentation(data[1], lineChartDataConfig);
    lineChartRepresentation = lineChartRepresentation.map(d => ({
      ...d,
      type: "line",
      key: lineChartDataConfig.chartValue.displayName || lineChartDataConfig.chartValue.aggregationType,
      legendDisplayName: lineChartAggregationType,
    }));

    return [...barChartRepresentation, ...lineChartRepresentation];
  }

  private getDataConfigByType(dataConfig: any, type: "BarChart" | "LineChart") {
    const chartXAxis = dataConfig.chartXAxis || dataConfig.chartRows?.[0];
    const chartColumns = dataConfig.chartColumns ? dataConfig.chartColumns[0] : dataConfig.chartSeries;
    const { chartValues } = dataConfig;
    
    if (type === "BarChart") {
      // Use destructuring to exclude chartRows and chartValues without mutation
      const { chartRows: _unused1, chartValues: _barChartValues, ...barChartBase } = dataConfig;
      const barChartDataConfig = {
        ...barChartBase,
        chartXAxis,
        chartValue: chartValues[0],
        chartSeries: chartColumns,
      };
      return barChartDataConfig;
    }
    
    // Use destructuring to exclude chartRows and chartValues without mutation
    const { chartRows: _unused2, chartValues: _lineChartValues, ...lineChartBase } = dataConfig;
    const lineChartDataConfig = {
      ...lineChartBase,
      chartXAxis,
      chartValue: chartValues[1],
      chartSeries: "",
    };
    return lineChartDataConfig;
  }

  private getLocalizedAggregationType(type: string, fieldName?: string) {
    switch (type) {
      case "count":
        return this.localize.getLocalizedString("_CombinationChart.Legend.Line.Count_");
      case "average":
        return this.localize.getLocalizedStringWithTokenReplacement(
          "_CombinationChart.Legend.Line.Average_",
          { fieldName }
        );
      case "sum":
        return this.localize.getLocalizedStringWithTokenReplacement("_CombinationChart.Legend.Line.Sum_", { fieldName });
      case "min":
        return this.localize.getLocalizedStringWithTokenReplacement("_CombinationChart.Legend.Line.Min_", { fieldName });
      case "max":
        return this.localize.getLocalizedStringWithTokenReplacement("_CombinationChart.Legend.Line.Max_", { fieldName });
      default:
        return type;
    }
  }
}

export function createAngularCompatibleCombinationService(
  queuedHttp: QueuedHttp,
  apiPath: ApiPathLike,
  localize: LocalizeLike,
  barChartBackendService: ChartBackendLike,
  lineChartBackendService: ChartBackendLike
) {
  const service = new CombinationChartBackendServiceTS(
    queuedHttp,
    apiPath,
    localize,
    barChartBackendService,
    lineChartBackendService
  );
  return {
    fetchJson: service.fetchJson.bind(service),
    getRepresentation: service.getRepresentation.bind(service),
  };
}


